import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { useAuth0 } from '@auth0/auth0-react';

import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery,
  type Theme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import qr from 'assets/images/doors-inspection-checked-qrcode.png';
import lcwLogo from 'assets/images/learner-credential-wallet.png';
import velocityLogo from 'assets/images/velocity-wallet.png';

type Props = {
  onClose: () => void;
};

type PageProps = {
  onClick: (next?: FC<PageProps>) => void;
};

export const ConnectCredWalletDialog: FC<Props> = ({ onClose }) => {
  const intl = useIntl();
  const [PageComponent, setPageComponent] = useState(() => LcwPage);

  return (
    <>
      <DialogContent>
        <PageComponent onClick={(next) => (next ? setPageComponent(() => next) : onClose())} />
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onClose}>
          {intl.formatMessage({ id: 'layout.hamburger.close' })}
        </Button>
      </DialogActions>
    </>
  );
};

const SelectionPage: FC<PageProps> = ({ onClick }) => {
  const intl = useIntl();
  return (
    <>
      <Typography sx={{ fontWeight: 700, height: '1.5rem' }}>
        {intl.formatMessage({ id: 'experience.wallet.selection.heading' })}
      </Typography>

      <Box sx={{ maxWidth: 400, pt: 2.2 }}>
        <Typography sx={{ fontWeight: 700 }}>
          {intl.formatMessage({ id: 'experience.wallet.selection.line1' })}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem' }}>
          {intl.formatMessage({ id: 'experience.wallet.selection.line2' })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', pt: 4 }}>
        <Typography sx={{ fontWeight: 700, height: '1.5rem', pb: 2.2 }}>Select the wallet you currently use</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ cursor: 'pointer' }}>
              <CardActionArea onClick={() => onClick(LcwPage)}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                  <Box sx={{ cursor: 'pointer' }}>
                    <img src={lcwLogo} alt="LCW logo" style={{ height: '60px', border: 0 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Learner Credential Wallet</Typography>
                  <Typography sx={{ fontSize: '0.85rem' }}>Digital Credential Consortium</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
           {/*
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ cursor: 'pointer' }}>
              <CardActionArea onClick={() => onClick(VelocityPage)}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                  <Box sx={{ cursor: 'pointer' }}>
                    <img src={velocityLogo} alt="Velocity logo" style={{ height: '60px', border: 0 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Velocity Career Wallet</Typography>
                  <Typography sx={{ fontSize: '0.85rem' }}>Verifiable Private Credentials</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          */}
        </Grid>
      </Box>
    </>
  );
};

const LcwPage: FC<PageProps> = ({ onClick }) => {
  const intl = useIntl();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { getAccessTokenSilently } = useAuth0();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await axios.post('/api/v2/lcw/qr-session', null, {
          headers: { 'x-access-token': `Bearer ${token}` },
        });
        const { transaction_id, qr_url } = data as { transaction_id: string; qr_url: string };
        const pollPath = `/api/v2/exchanges/${transaction_id}`;
        setQrUrl(qr_url);

        if (cancelled) return;

        const dataUrl = await QRCode.toDataURL(qr_url, { width: 300, margin: 2 });

        if (cancelled) return;
        setQrDataUrl(dataUrl);

        const schedulePoll = () => {
          if (completedRef.current || cancelled) return;
          pollIntervalRef.current = setTimeout(async () => {
            if (completedRef.current || cancelled) return;
            try {
              const pollRes = await axios.get(pollPath, {
                params: { _t: Date.now() },
                headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
              });
              const pollData = pollRes.data;
              const vp = pollData?.verifiablePresentation;
              const isComplete =
                (vp && Array.isArray(vp.verifiableCredential) && vp.verifiableCredential.length > 0) ||
                Array.isArray(pollData?.credentials) ||
                pollData?.status === 'complete';
              if (isComplete) {
                completedRef.current = true;
                if (!cancelled) onClick(FinalPage);
              } else {
                schedulePoll();
              }
            } catch (err) {
              console.warn('LCW poll error:', err);
              schedulePoll();
            }
          }, 3000);
        };

        schedulePoll();
      } catch {
        if (!cancelled) setError('Could not start wallet session. Please try again.');
      }
    };

    init();

    return () => {
      cancelled = true;
      if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
    };
  }, []); // eslint-disable-line

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', pt: 2 }}>
        {error ? (
          <Typography color="error" sx={{ pt: 2 }}>
            {error}
          </Typography>
        ) : qrDataUrl ? (
          <>
            <Typography sx={{ fontSize: '0.875rem', pb: 2, textAlign: 'center' }}>
              {intl.formatMessage({ id: 'experience.wallet.lcw.scanQr' })}
            </Typography>
            <img src={qrDataUrl} alt="LCW QR code" style={{ height: '300px', width: '300px' }} />
            {qrUrl && isMobile && (
              <Typography
                component="a"
                href={qrUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.875rem', mt: 1 }}
              >
                If you cannot access the QR code, you can use this link to open your wallet.
              </Typography>
            )}
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', pt: 1 }}>
              {intl.formatMessage({ id: 'experience.wallet.lcw.waiting' })}
            </Typography>
          </>
        ) : (
          <Box sx={{ pt: 4, pb: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </>
  );
};

const VelocityPage: FC<PageProps> = ({ onClick }) => {
  const intl = useIntl();

  return (
    <>
      <Typography sx={{ fontWeight: 700, height: '1.5rem' }}>
        {intl.formatMessage({ id: 'experience.wallet.selection.heading' })}
      </Typography>

      <Box sx={{ maxWidth: 400, pt: 2.2 }}>
        <Typography sx={{ fontWeight: 700 }}>
          {intl.formatMessage({ id: 'experience.wallet.selection.line1' })}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem' }}>
          {intl.formatMessage({ id: 'experience.wallet.selection.line2' })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
        <img src={qr} alt="Velocity logo" style={{ height: '300px', border: 0 }} />

        <Button
          color="primary"
          size="large"
          variant="contained"
          sx={{ textTransform: 'none', px: '2.5rem' }}
          onClick={() => onClick(FinalPage)}
        >
          Next
        </Button>
      </Box>
    </>
  );
};

const FinalPage: FC<PageProps> = ({ onClick }) => {
  const intl = useIntl();

  return (
    <>
      <Box sx={{ pt: 2.2 }}>
        <Typography sx={{ fontWeight: 700 }}>
          {intl.formatMessage({ id: 'experience.wallet.confirmation.line3' })}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem' }}>
          {intl.formatMessage({ id: 'experience.wallet.confirmation.line4' })}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', pt: 1 }}>
          {intl.formatMessage({ id: 'experience.wallet.confirmation.line6' })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', pt: 2.2 }}>
        <Typography sx={{ fontSize: '0.875rem', mb: '1rem', alignSelf: 'flex-start' }}>
          {intl.formatMessage({ id: 'experience.wallet.confirmation.line5' })}
        </Typography>
        <Button
          color="primary"
          size="large"
          variant="contained"
          sx={{ textTransform: 'none', px: '2.5rem' }}
          onClick={() => onClick()}
        >
          {intl.formatMessage({ id: 'experience.wallet.confirmation.button' })}
        </Button>
      </Box>
    </>
  );
};
